import React, { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { CategorizationWithConfig } from "../../../../configMap/interfaces/category";
import SelectEmptyState from "../SelectEmptyState";
import LinkIcon from "../LinkIcon";
import { getInLanguageOrDefault } from "../../../../common/utilities/language";
import CategoryVisibleValue from "../CategoryVisibleValue";
import { Button, Tooltip } from "antd";
import CategorizationSelect from "./CategorizationSelect";
import { EventTriggerType } from "../../../interfaces/categorizationEvents";
import { OptionType } from "../../../interfaces/classifier";
import type { CallEvents } from "../../../hooks/useCategorizationEvents";
import { DefaultOptionType } from "antd/es/select";

type Props = {
  disabled: boolean;
  categorizationWithConfig: CategorizationWithConfig;
  tabId: string;
  selectedCategory: {
    value: string | undefined;
    visibleValue: string | undefined;
  } | null;
  categorizationOptions: Array<OptionType>;
  handleChange: (key: string, callback: () => void) => Promise<void>;
  status: "" | "error" | "warning" | undefined;
  isCategorizationDisabledByParent: boolean | undefined;
  disabledParentTooltip: string;
  callEvents: CallEvents;
  isCategorizationLoading: boolean;
  emptySearchResult: boolean;
  loading: boolean;
  isLoadingValue: boolean;
  onFocus: () => void;
  onBlur: () => void;
  onSearch: (value: string) => void;
  filterOption: (input: string, option?: DefaultOptionType) => boolean;
  className: string;
  isDerivedType?: boolean;
};

const CategorizationUI: FC<Props> = ({
  categorizationWithConfig,
  disabled,
  tabId,
  selectedCategory,
  categorizationOptions,
  handleChange,
  status,
  isCategorizationDisabledByParent,
  disabledParentTooltip,
  callEvents,
  isCategorizationLoading,
  emptySearchResult,
  loading,
  isLoadingValue,
  onFocus,
  onBlur,
  onSearch,
  filterOption,
  className,
  isDerivedType = false,
}) => {
  const { label } = categorizationWithConfig.categorization;

  const {
    i18n: { language },
  } = useTranslation();

  const containerClassName = useMemo(() => {
    return isDerivedType ? "categorization-derived__container" : "";
  }, [isDerivedType]);

  const buttonClassName = useMemo(() => {
    const classes = ["categorization-derived__button"];

    if (disabled) {
      classes.push("disabled");
    }

    return classes.join(" ");
  }, [disabled]);

  return (
    <div className="classifier-item__container">
      <div className="classifier-item__container">
        <div className="classifier-item__data-wrapper">
          <div className="classifier-item__label-wrapper">
            <label>
              {`${getInLanguageOrDefault(label, language)} ${
                categorizationWithConfig.required ? "*" : ""
              }`}
              <LinkIcon
                categorization={categorizationWithConfig.categorization}
              />
            </label>
            <CategoryVisibleValue
              visibleValue={selectedCategory?.visibleValue}
            />
          </div>
        </div>
        <div className={containerClassName}>
          <Tooltip
            title={
              isCategorizationDisabledByParent
                ? disabledParentTooltip
                : undefined
            }
          >
            <span
              style={{
                display: "block",
                flex: 1,
                minWidth: 0,
                cursor: isCategorizationDisabledByParent
                  ? "not-allowed"
                  : undefined,
              }}
            >
              <CategorizationSelect
                categorization={categorizationWithConfig.categorization}
                value={selectedCategory?.value}
                visibleValue={selectedCategory?.visibleValue}
                handleChange={handleChange}
                status={status}
                callEvents={callEvents}
                onFocus={onFocus}
                onBlur={onBlur}
                onSearch={onSearch}
                filterOption={filterOption}
                notFoundContent={
                  <SelectEmptyState
                    loading={loading}
                    hasNoResult={emptySearchResult}
                  />
                }
                disabled={
                  disabled ||
                  isCategorizationDisabledByParent ||
                  isCategorizationLoading
                }
                tabId={tabId}
                categorizationOptions={categorizationOptions}
                loading={isCategorizationLoading}
                isLoadingValue={isLoadingValue}
                className={className}
              />
            </span>
          </Tooltip>
          {isDerivedType ? (
            <Button
              disabled={disabled}
              onClick={() =>
                !disabled &&
                callEvents(
                  EventTriggerType.ON_CLICK,
                  categorizationWithConfig.categorization,
                )
              }
              className={buttonClassName}
              icon={<i className="bi bi-arrow-clockwise" />}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default CategorizationUI;
